import { gql, TypedDocumentNode } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { getTimeLeftInFreeTrial } from "@components/account/utils";
import { useAuth, useWatchUserWsEvent } from "@hooks";
import { useLDClient } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import React, {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
} from "react";

import {
  GetUserAccountDetailsQuery,
  GetUserAccountDetailsQueryVariables,
} from "./use-account.generated";

const accountContext = createContext<{
  isConfirmed: boolean;
  isSubscribed: boolean;
  isUserOnFreeTrial: boolean;
  accountType: string | null;
  refetchAccountDetails: () => void;
}>(null!);

const GET_USER_ACCOUNT_DETAILS: TypedDocumentNode<
  GetUserAccountDetailsQuery,
  GetUserAccountDetailsQueryVariables
> = gql`
  query GetUserAccountDetails {
    user {
      uuid
      isConfirmed
      account {
        uuid
        isSubscribed
        accountType
        freeTrialExpiresAt
      }
      teams {
        uuid
        organization {
          uuid
        }
      }
    }
  }
`;

const getAccountTypeForMixpanel = (
  accountType: string | null,
  isUserOnFreeTrial: boolean,
) => {
  if (accountType === "unlimited") return "paid_pro";
  if (accountType === "basic" && isUserOnFreeTrial)
    return "pro_trial";
  return "free";
};

export const useAccount = () => {
  return useContext(accountContext);
};

export function useProvideAccount() {
  const { userUuid } = useAuth();
  const ldClient = useLDClient();

  const [
    fetchAccountDetails,
    {
      data: userAccountDetails,
      refetch: refetchAccountDetails,
      called,
    },
  ] = useLazyQuery(GET_USER_ACCOUNT_DETAILS);

  const accountType = userUuid
    ? userAccountDetails?.user?.account?.accountType || null
    : null;
  const isConfirmed = userUuid
    ? userAccountDetails?.user?.isConfirmed === true
    : false;
  const isSubscribed = userUuid
    ? userAccountDetails?.user?.account?.isSubscribed === true
    : false;

  const isUserOnFreeTrial = (() => {
    if (!userUuid) return false;

    const user = userAccountDetails?.user;
    if (!user) return false;

    const { freeTrialExpiresAt } = user.account || {
      isSubscribed: null,
      accountType: null,
    };

    const timeLeftInFreeTrial = getTimeLeftInFreeTrial(
      freeTrialExpiresAt,
    );
    return !timeLeftInFreeTrial.isExpired;
  })();

  const orgUuids = useMemo(() => {
    if (!userUuid) return [];

    return Array.from(
      new Set(
        userAccountDetails?.user?.teams
          ?.map((team) => team?.organization?.uuid)
          .filter(Boolean),
      ),
    );
  }, [userUuid, userAccountDetails?.user?.teams]);

  const safeRefetchAccountDetails = () => {
    if (called) {
      refetchAccountDetails();
    } else {
      fetchAccountDetails();
    }
  };

  const fetchAccountDetailsEvent = useEffectEvent(() => {
    fetchAccountDetails();
  });

  useEffect(() => {
    if (userUuid) {
      fetchAccountDetailsEvent();
    }
  }, [userUuid]);

  // Update Mixpanel
  useEffect(() => {
    mixpanel.register({
      "Account Type": getAccountTypeForMixpanel(
        accountType,
        isUserOnFreeTrial,
      ),
    });
  }, [accountType, isUserOnFreeTrial]);

  useWatchUserWsEvent({
    onMessage: safeRefetchAccountDetails,
    eventName: "user_update",
  });

  // Update LaunchDarkly whenever the user changes
  useEffect(() => {
    const ldContext = {
      kind: "multi",
      user: {
        key: `user-uuid-${userUuid}`,
        accountType: accountType,
        organizations: orgUuids,
      },
    };
    ldClient!.identify(ldContext);
  }, [userUuid, accountType, orgUuids, ldClient]);

  return {
    isConfirmed,
    isSubscribed,
    isUserOnFreeTrial,
    accountType,
    refetchAccountDetails: safeRefetchAccountDetails,
  };
}

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAccount().
export function AccountProvider(
  props: React.PropsWithChildren<void>,
) {
  const account = useProvideAccount();

  return (
    <accountContext.Provider value={account}>
      {props.children}
    </accountContext.Provider>
  );
}
